import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  Chip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CloudUpload, Delete, Edit } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { parseExcelQuestions } from './mockInstructorApi';
import { fetchExamNames, fetchExamDetails } from './api';

const DIFFICULTY_LEVELS = ['easy', 'medium', 'hard'];

const CreateTestForm = ({ onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('');
    const [topics, setTopics] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    duration: 60,
    passingScore: 70,
    instructions: ''
  });

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const data = await fetchExamNames();
        setCategories(data.map(exam => ({ id: exam, name: exam }))); // Adapt to expected format
      } catch (error) {
        setError('Failed to fetch exam names.');
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData(prev => ({
    ...prev,
    [name]: value,
    ...(name === 'category' ? { subcategory: '', topic: '' } : {}), // Reset subcategory and topic
    ...(name === 'subcategory' ? { topic: '' } : {}), // Reset topic
  }));

    if (name === 'category') {
        fetchSubcategories(value);
    }

    if(name === 'subcategory') {
        setSelectedSubject(value)
    }
};

const fetchSubcategories = async (examName) => {
    if (!examName) {
      return
    }
    setLoading(true);
    try {
      const details = await fetchExamDetails(examName);
      if (details && details.Subjects) {
        const selectedCategory = categories.find(cat => cat.name === examName);
        if (selectedCategory) {
            selectedCategory.subcategories = Object.keys(details.Subjects);
            setCategories([...categories]); // Trigger re-render
        }
      } else {
        setError('Failed to fetch subcategories or no subcategories found.');
      }
    } catch (error) {
      setError('Failed to fetch subcategories.');
    } finally {
      setLoading(false);
    }
};

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError('');

      // Read the Excel file
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Validate the Excel structure
          const requiredColumns = ['question', 'answers', 'correctAnswer', 'topic', 'difficulty'];
          const headers = Object.keys(jsonData[0] || {});
          const missingColumns = requiredColumns.filter(col => !headers.includes(col));

          if (missingColumns.length > 0) {
            setError(`Missing required columns: ${missingColumns.join(', ')}`);
            return;
          }

          // Parse the questions using the mock API
          const result = await parseExcelQuestions(file);
          if (result.success) {
            setPreviewData(result.data);
            setActiveStep(1);
          } else {
            setError('Failed to parse questions. Please check the file format.');
          }
        } catch (error) {
          setError('Invalid file format. Please use the correct Excel template.');
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      setError('Error processing file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      // Here you would typically send both formData and previewData to your API
      console.log('Submitting test:', { formData, questions: previewData });
      onClose();
    } catch (error) {
      setError('Failed to create test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Basic Details', 'Upload Questions', 'Review & Submit'];

    useEffect(() => {
        const fetchTopics = async () => {
            if (formData.category && formData.subcategory) {
                try {
                    const details = await fetchExamDetails(formData.category);
                    const subjectData = details?.Subjects?.[formData.subcategory];
                    if (subjectData && subjectData.Topics) {
                        setTopics(subjectData.Topics);
                    } else {
                        setTopics([]);
                    }
                } catch (error) {
                    console.error("Error fetching topics:", error);
                    setTopics([]);
                }
            }
        };

        fetchTopics();
    }, [formData.category, formData.subcategory]);

const getSelectedCategory = () => categories.find(cat => cat.name === formData.category) || null;

    const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Exam Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Exam</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  label="Exam"
                  required
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!formData.category}>
                <InputLabel>Subject</InputLabel>
                <Select
                  name="subcategory"
                  value={formData.subcategory}
                  onChange={handleInputChange}
                  label="Subject"
                >
                  {(getSelectedCategory()?.subcategories ?? []).map((sub) => (
                    <MenuItem key={sub} value={sub}>
                      {sub}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {topics.length > 0 && (
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Topic</InputLabel>
                        <Select
                            name="topic"
                            value={formData.topic}
                            onChange={handleInputChange}
                            label="Topic"
                        >
                            {topics.map((topic) => (
                                <MenuItem key={topic} value={topic}>
                                    {topic}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                name="duration"
                type="number"
                value={formData.duration}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Passing Score (%)"
                name="passingScore"
                type="number"
                value={formData.passingScore}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instructions"
                name="instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Box>
              <Typography variant="h6" gutterBottom>
              Upload Questions Excel File
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload an Excel/CSV file with the following columns:
              <ul>
                <li>question: The question text</li>
                <li>answers: Multiple choice options separated by | character</li>
                <li>correctAnswer: Index of the correct answer (0-based)</li>
                <li>topic: Topic/subject of the question</li>
                <li>difficulty: easy, medium, or hard</li>
                <li>hints: Optional hints for students</li>
                <li>imageUrl: Optional URL for question images</li>
              </ul>
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                href="/test_questions_template.csv"
                download
                sx={{ mr: 2 }}
              >
                Download Template CSV
              </Button>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
              >
                Upload Question File
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                />
              </Button>
            </Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {previewData && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Question</TableCell>
                      <TableCell>Topic</TableCell>
                      <TableCell>Difficulty</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((question, index) => (
                      <TableRow key={index}>
                        <TableCell>{question.question}</TableCell>
                        <TableCell>{question.topic}</TableCell>
                        <TableCell>
                          <Chip
                            label={question.difficulty}
                            color={
                              question.difficulty === 'easy'
                                ? 'success'
                                : question.difficulty === 'medium'
                                ? 'warning'
                                : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small">
                            <Edit />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Exam Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Exam Information
                  </Typography>
                  <Typography>Name: {formData.name}</Typography>
                  <Typography>Category: {formData.category}</Typography>
                  <Typography>Subcategory: {formData.subcategory}</Typography>
                  <Typography>Duration: {formData.duration} minutes</Typography>
                  <Typography>Passing Score: {formData.passingScore}%</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Questions Summary
                  </Typography>
                  <Typography>Total Questions: {previewData?.length || 0}</Typography>
                  <Typography>
                    Difficulty Distribution:
                    {DIFFICULTY_LEVELS.map(level => (
                      <Chip
                        key={level}
                        label={`${level}: ${previewData?.filter(q => q.difficulty === level).length || 0}`}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    ))}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {renderStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={() => setActiveStep((prev) => prev - 1)}
          sx={{ mr: 1 }}
        >
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <LoadingButton
            variant="contained"
            onClick={handleSubmit}
            loading={loading}
          >
            Submit Questions to Exam
          </LoadingButton>
        ) : (
          <Button
            variant="contained"
            onClick={() => setActiveStep((prev) => prev + 1)}
            disabled={
              (activeStep === 0 && !formData.name) ||
              (activeStep === 1 && !previewData)
            }
          >
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CreateTestForm;
